import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { CONTACTS_DATA_SERVICE, ContactsDataService } from '../../../../services/contacts/contacts-data.service';
import {
  ResourceCountry,
  ResourceIsoLanguage,
  ResourceProvinces,
  ResourceSalutation
} from '../../../../models/resources/resources.model';
import { UploadHelper } from '../../../../helper/uploadHelper';
import { IFileDocument } from '../../../../models/shared.model';
import { RESOURCES_DATA_SERVICE, ResourcesDataService } from '../../../../services/resources/resources-data.service';
import { PhoneNumberHelper } from '../../../../helper/phone-number.helper';
import { UserProfileShareService } from '../../../../services/shared-service/user-profile-share.service';
import { combineLatest, Observable, Subject, Subscription } from 'rxjs';
import { ProfileData } from '../../../../models/profile/profile.model';
import { AddressHelper } from '../../../../helper/address.helper';
import { TranslationDirective } from '../../directives/translation.directive';
import { ActionHelper } from '../../../../helper/action.helper';
import { LeadHelper } from '../../../../helper/lead.helper';
import { ContactHelper } from '../../../../helper/contact.helper';
import { LanguageHelper } from '../../../../helper/language.helper';
import { ContactCompany, ContactType } from '../../../../models/contacts/contacts.model';
import { debounceTime } from 'rxjs/operators';
import {
  LAST_SAVED_COUNTRY_CODE,
  LAST_SELECTED_ADDITIONAL_MOBILE_PHONE_PREFIX, LAST_SELECTED_COUNTRY_CODE, LAST_SELECTED_LANGUAGE_CODE,
  LAST_SELECTED_MOBILE_PHONE_PREFIX,
  LAST_SELECTED_PHONE_PREFIX
} from '../../../../constants/constants';

@Component({
  selector: 'fi-contact-form',
  templateUrl: './contact-form.component.html',
  styleUrls: ['./contact-form.component.scss'],
  standalone: false,
})
export class ContactFormComponent implements OnInit, OnDestroy, OnChanges {

  @Input() contactForm: UntypedFormGroup;
  @Input() requestForm: UntypedFormGroup;
  @Input() isNewLeadForm: boolean;
  @Input() showPrivate: boolean;
  @Input() isLinkForm: boolean;
  @Input() additionalContact: boolean;
  @Input() compendiumActionEnabled: boolean = false;
  @Input() compendiumVisible: boolean = false;
  @Input() privacy = {
    contact: false,
    documented: true
  };
  @Input() isUpdate: boolean;
  @Input() isMobileNewLead: boolean;


  @Output() privacyDocumentUploaded = new EventEmitter<IFileDocument>();
  @Output() onMarketingChanges = new EventEmitter<{ consentThirdParties: boolean, consentMarketing: boolean }>();
  @Output() hideErrors = new EventEmitter<boolean>();
  @ViewChildren('inputs') inputs: QueryList<ElementRef<HTMLElement>>;
  @ViewChild('formEl') formEl: ElementRef<HTMLElement>;

  showUpdateContactMessage = false;
  contactExistMessage: boolean;
  provinceInputIsFocused = false;
  leadCreationFailureError = false;
  leadCreationFailureRelationshipManagementError = false;
  consentThirdParties = false;
  consentMarketing = false;
  accessCompendium = false;
  showErrors = false;
  showMaxLengthErrors = false;
  resourceSalutation: ResourceSalutation[];
  resourceLanguages: ResourceIsoLanguage[];
  resourceCountries: ResourceCountry[];
  resourceProvinces: ResourceProvinces[];

  profileData: Observable<ProfileData>;
  privacyDocument: IFileDocument;
  numberPrefixes: { code: string, name: string }[] = [];

  readonly actionHelper = ActionHelper;
  updateContact = false;
  emails = false;
  mobileNumbers = false;
  emailExists: boolean;
  mobileExists: boolean;
  tabs: { Name: string, Id: string }[] = [];
  needsAtLeastOneEmailOrPhone = false;
  provinceCountryCodes: string[];
  notPrivateContactType = [ContactHelper.CONTACT_TYPE_PLANER, ContactHelper.CONTACT_TYPE_TRADE];
  contactCompanies: ContactCompany[] = [];
  searchString: string;
  selectedCompany: any;

  private contactTypes: ContactType[] = [];
  private searchInputSubject$ = new Subject<void>();
  private searchInputSubscription: Subscription;
  private subscription: Subscription;

  constructor(
    private fb: UntypedFormBuilder,
    private readonly sharedProfileService: UserProfileShareService,
    @Inject(CONTACTS_DATA_SERVICE) private contactsDataService: ContactsDataService,
    @Inject(RESOURCES_DATA_SERVICE) private resourcesDataService: ResourcesDataService,
    private ref: ChangeDetectorRef
  ) {
    this.profileData = sharedProfileService.getProfile();

    this.searchInputSubscription = this.searchInputSubject$
      .pipe(debounceTime(500)).subscribe(() => this.fireSearch());

    AddressHelper.getProvinceCountryCodes().then(data => {
      this.provinceCountryCodes = data;
    });

    this.contactsDataService.getContactTypes().subscribe(( { success, data } ) => {
      if ( success ) {
        this.contactTypes = data;
        this.setContactTypesTabs();
      }
    })
  }

  ngOnChanges( changes: SimpleChanges ): void {
    if ( changes.showPrivate ) {
      this.setContactTypesTabs();
    }
  }

  ngOnInit(): void {

    if ( this.contactForm ) {
      if ( !this.isUpdate ) {
        this.contactForm.controls.ContactMethodId.setValue(null);
      }

      if ( this.contactForm.controls.CountryCode?.value ) {
        this.setCountyCode(this.contactForm.controls.CountryCode?.value, false);
      }
    }

    this.getCountries();
    this.getSalutation();
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.searchInputSubscription?.unsubscribe();
  }

  getCountries(): void {
    AddressHelper.getCountries().then(countries => {

      //@ts-ignore
      this.resourceCountries = countries;
      this.numberPrefixes = PhoneNumberHelper.getCodeList(this.resourceCountries);

      this.subscription = combineLatest([
        this.profileData,
        this.resourcesDataService.getResourceIsoLanguages()]).subscribe(data => {

        if ( data[1]?.success ) {
          this.resourceLanguages = LanguageHelper.prepareOptions(data[1].data);
        }

        if ( this.contactForm && data[0] ) {
          if ( this.resourceLanguages ) {
            this.applyLanguageCode(this.contactForm.controls.LanguageCode.value || data[0].User.LanguageCode);
          }
          this.setResources();
        }
      });
    });
  }

  getSalutation(): void {
    //@ts-ignore
    this.resourceSalutation = [];
    this.resourcesDataService.getResourceSalutation().subscribe(data => {

      if ( !data?.success ) {
        console.log('wrong  Salutation data.')
        return;
      }

      this.resourceSalutation = data.data.sort(( a, b ) => a.SortOrder - b.SortOrder);
      // if (this.contactForm && data) {
      //   if ((!this.contactForm.controls.Salutation || !this.contactForm.controls.Salutation.value) && this.resourceSalutation && this.resourceSalutation.length) {
      //     this.contactForm.controls.Salutation.setValue(this.resourceSalutation[0].Id);
      //   }
      // }
    });
  }

  getCompanyName = ( item: ContactCompany ) => item.Name;

  onContactTypeChange( data: string ) {
    const typeId = +data;
    this.contactForm?.controls.ContactTypeId.setValue(+data);
    if ( !this.isUpdate || this.additionalContact ) {
      if ( typeId === ContactHelper.CONTACT_TYPE_TRADE || typeId === ContactHelper.CONTACT_TYPE_PLANER ) {
        this.consentMarketing = true;
        this.consentChanges();
      } else {
        this.consentMarketing = false;
        this.consentChanges();
      }
    }
  }

  onCompanySelect( data: string ): void {
    this.selectedCompany = data;
    this.contactForm.controls.Company.setValue(this.selectedCompany);
  }

  onCompanySearch( searchKey: string ): void {
    this.searchString = searchKey;
    this.selectedCompany = searchKey;
    this.contactForm.controls.Company.setValue(this.searchString);
    if ( this.searchString ) {
      this.searchInputSubject$.next();
    }
  }

  fireSearch(): void {
    this.contactsDataService.getContactCompanies(this.searchString).subscribe(( data ) => {
      if ( data.success ) {
        this.contactCompanies = data.data;
      }
    })
  }

  getResourceProvinces( countryCode: string ): void {
    AddressHelper.getProvinces(countryCode).then(data => {
      this.resourceProvinces = data;
    })
  }

  setResources(): void {
    if ( this.resourceCountries ) {
      const countryCode = sessionStorage.getItem(LAST_SAVED_COUNTRY_CODE);

      if ( countryCode ) {
        const prefix = this.resourceCountries.find(item => item.IsoCode === countryCode)?.PhonePrefix;

        if ( prefix ) {
          if ( !this.contactForm.controls.PhonePrefix.value && !this.contactForm.controls.Phone.value ) {
            this.contactForm.controls.PhonePrefix.setValue(prefix);
          }

          if ( !this.contactForm.controls.MobilePrefix.value && !this.contactForm.controls.Mobile.value ) {
            this.contactForm.controls.MobilePrefix.setValue(prefix);
          }
          if ( !this.contactForm.controls.PhoneOtherPrefix.value && !this.contactForm.controls.PhoneOther.value ) {
            this.contactForm.controls.PhoneOtherPrefix.setValue(prefix);
          }
        }

        if ( !this.contactForm.controls.CountryCode.value ) {
          this.contactForm.controls.CountryCode.setValue(countryCode);

          if ( !this.resourceProvinces ) {
            this.getResourceProvinces(countryCode);
          }
        }

      } else {
        if ( !this.resourceProvinces || this.contactForm.controls.CountryCode.value ) {
          this.getResourceProvinces(this.contactForm.controls.CountryCode.value);
        }
      }
    }
  }

  isInvalidProvinceCode( showErrors = false ): boolean {
    return false;
    /*
    if (showErrors) {
      this.showErrors = showErrors;
    }

    return this.showErrors &&
        this.provinceCountryCodes.includes(this.contactForm?.get('CountryCode').value) &&
        !this.contactForm?.get('ProvinceCode').value;

     */
  }

  isInvalidForm( form: string, fieldName: string ): boolean {
    if ( fieldName === 'ProvinceCode' ) {
      return this.isInvalidProvinceCode();
    }

    const filledAdditionalEmail = !!this.contactForm?.get('AdditionalEmail').value;
    const filledAdditionalPhone = !!this.contactForm?.get('PhoneOther').value;

    if ( filledAdditionalEmail ) {
      this.emails = true;
    }

    if ( filledAdditionalPhone ) {
      this.mobileNumbers = true;
    }

    if ( fieldName === 'Email' ) {
      this.isInvalidFormEmail(form, fieldName);
    }
    return (this.showErrors || this.showMaxLengthErrors) && this[form].controls[fieldName].invalid;
  }

  isInvalidFormEmail( form: string, fieldName: string ): any {
    const valid = this.contactForm?.get('Email')?.value?.match(/\S+@\S+\.\S+/);
    const notEmail = this.contactForm.controls.ContactMethodId.value as number;

    if ( this.contactForm?.get('Email')?.value ) {
      if ( valid ) {
        const emailInput = this.formEl?.nativeElement?.querySelector('input.Email') as HTMLInputElement;

        if ( !emailInput?.value.endsWith('@temp.finstral.com') ) {
        } else {
          emailInput.value = '';
        }
      } else {
      }
    } else {
      if ( notEmail !== 1 ) {
        return this[form].controls[fieldName].invalid;
      } else {
        return this[form].controls[fieldName].valid;
      }
    }

    if ( this.isUpdate && this.contactForm?.get('Email')?.value.length === 0 ) {
      //this.emailField?.nativeElement?.querySelector('input')?.focus();
    }
  }

  checkExistingEmail(): void {
    this.contactsDataService.getContactsByEmail(this.contactForm.controls.Email.value)
      .subscribe(( { success, data } ) => {
        if ( success ) {
          this.emailExists = true;
          this.updateContact = true;
          if ( this.isUpdate ) {
            this.showUpdateContactMessage = true;
          }

          if ( this.additionalContact ) {
            data.Company = this.contactForm.controls.Company.value ?? null;
            if ( data.ContactTypeId === ContactHelper.CONTACT_TYPE_PRIVATE ) {
              data.ContactTypeId = ContactHelper.CONTACT_TYPE_TRADE
            }
          }

          if ( data.Email ) {
            if ( data.Mobile ) {
              this.contactForm.controls.ContactMethodId.setValue(3);
            } else {
              this.contactForm.controls.ContactMethodId.setValue(1);
            }
          } else {
            this.contactForm.controls.ContactMethodId.setValue(3);
          }

          this.contactForm.patchValue({ ...data, Salutation: data?.SalutationId });
          this.consentMarketing = data?.ConsentMarketing;
          this.consentThirdParties = data?.ConsentThirdParties;
          this.consentChanges();
        } else {
          this.emailExists = false;
          this.updateContact = false;
        }
      });
  }

  hideInvalidError(): void {
    this.showErrors = false;
    this.contactExistMessage = false;
    this.hideErrors.emit(this.showErrors);
  }


  checkIfUpdateContent( fieldCode: number = null ): void {
    if ( this.updateContact ) {
      this.showUpdateContactMessage = true;
    }
    this.changePreference(fieldCode);
  }

  removeIfUpdateContent( fieldCode: number = null ): void {
    if ( this.updateContact ) {
      this.showUpdateContactMessage = false;
    }
    this.changePreference(fieldCode);
  }

  removeUpdateExternalEmail(): void {
    if ( this.updateContact ) {
      setTimeout(() => {
        this.showUpdateContactMessage = false;
      }, 200);
    }
  }

  get hasFilledInvalidInputs(): boolean {
    for ( const key in this.contactForm?.controls ) {
      if ( this.contactForm.controls[key].value && this.contactForm.controls[key].invalid ) {
        return true;
      }
    }

    return false;
  }

  get hasEmptyRequiredInputs(): boolean {
    if ( this.needsAtLeastOneEmailOrPhone ) {
      return true;
    }

    for ( const key in this.contactForm?.controls ) {
      if ( !this.contactForm.controls[key].value && this.contactForm.controls[key].invalid ) {
        return true;
      }
    }

    return false;
  }

  focusPreviousElement( $event: any ): void {
    const currentElem = $event.target as HTMLInputElement;
    if ( currentElem.value.length === 0 ) {
      (currentElem.previousElementSibling as HTMLInputElement).focus();
    }
  }

  uploadImage( $event: Event, fileField: string ): void {
    const inputField = $event.target as HTMLInputElement;
    UploadHelper.uploadFile(inputField.files[0])
      .then(res => {
        inputField.value = '';
        if ( fileField === 'privacyDocument' ) {
          this.privacyDocument = res;
          this.privacyDocumentUploaded.emit(this.privacyDocument);
        }
      })
      .catch(err => {
        console.log(err);
      });
  }

  removeDocument( $event: Event, field: string ): void {
    $event.stopPropagation();

    if ( field === 'privacyDocument' ) {
      this.privacyDocument = undefined;
      this.privacyDocumentUploaded.emit(this.privacyDocument);
    }
  }

  setPhonePrefix( data: string ): void {
    if ( this.isNewLeadForm ) {
      sessionStorage.setItem(LAST_SELECTED_PHONE_PREFIX, data);
    }
    this.contactForm?.controls.PhonePrefix.setValue(data)
  }

  setMobilePhonePrefix( data: string ): void {
    if ( this.isNewLeadForm ) {
      sessionStorage.setItem(LAST_SELECTED_MOBILE_PHONE_PREFIX, data);
    }
    this.contactForm?.controls.MobilePrefix.setValue(data)
  }

  setAdditionalPhoneOtherPrefix( data: string ): void {
    if ( this.isNewLeadForm ) {
      sessionStorage.setItem(LAST_SELECTED_ADDITIONAL_MOBILE_PHONE_PREFIX, data);
    }
    this.contactForm?.controls.PhoneOtherPrefix.setValue(data)
  }

  setLanguageCode( data: string ): void {
    if ( this.isNewLeadForm ) {
      sessionStorage.setItem(LAST_SELECTED_LANGUAGE_CODE, data);
    }
    this.contactForm?.controls.LanguageCode.setValue(data)
  }

  /**
   * The code can be any valid IETF language tag (user profile, session storage, an already
   * stored contact). Only codes offered by the IsoLanguages resource can be shown in the
   * select, so map it to the closest supported one before applying it.
   */
  private applyLanguageCode( code: string ): void {
    const control = this.contactForm?.controls.LanguageCode;

    if ( !control ) {
      return;
    }

    const resolved = LanguageHelper.resolveIsoCode(code, this.resourceLanguages);

    if ( resolved !== control.value ) {
      control.setValue(resolved);
    }
  }

  setCountyCode( data: string, resetProvince: boolean ): void {
    if ( this.isNewLeadForm ) {
      sessionStorage.setItem(LAST_SELECTED_COUNTRY_CODE, data);
    }

    this.contactForm?.controls.CountryCode.setValue(data);
    if ( resetProvince ) {
      this.contactForm?.controls.ProvinceCode.setValue('');
    }

    AddressHelper.getProvinces(data).then(data => {
      this.resourceProvinces = data;
    })
  }

  getDateNow( showTime = false ): string {
    const date = new Date();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    return date.toLocaleDateString().split('/').join('.') +
      (showTime ? ` ${(hours > 9 ? '' : '0') + hours}:${(minutes > 9 ? '' : '0') + minutes}` : '');
  }

  showDetails = false;

  get getContactAddressRequiredStar(): string {

    const leadTypeId = this.requestForm?.get('LeadTypeId')?.value;

    // BROSCHÜRE and MUSTER
    return (leadTypeId === LeadHelper.LEAD_TYPE_BROCHURE || leadTypeId === LeadHelper.LEAD_TYPE_MUSTER || leadTypeId === LeadHelper.LEAD_TYPE_AUSSCHREIBUNGSTEXTE) ? '*' : '';
  }

  get getEmailOrPhoneRequiredStar(): string {
    return this.checkIfEmailOrPhoneNeedsToBeFilled(false, true) ? '*' : '';
  }

  addEmail(): void {
    this.emails = !this.emails;
  }

  addMobileNumber(): void {
    this.mobileNumbers = !this.mobileNumbers;
  }

  toggleShowMore(): void {
    this.showDetails = !this.showDetails;

    setTimeout(() => {
      if ( this.showDetails ) {
        const input = this.inputs.toArray().find(x => x.nativeElement.classList.contains('LanguageCode'))?.nativeElement;

        if ( input ) {
          input.scrollIntoView({
            block: "start",
            behavior: "smooth",
          });
        }
      }
    }, 100)
  }

  get isMobileMusterBroschure(): boolean {
    if ( window.innerWidth > 768 ) {
      return false;
    }
    const leadTypeId = this.requestForm?.get('LeadTypeId')?.value;

    // BROSCHÜRE and MUSTER
    return leadTypeId === LeadHelper.LEAD_TYPE_BROCHURE || leadTypeId === LeadHelper.LEAD_TYPE_MUSTER;
  }

  private changePreference( fieldCode: number ): void {
    if ( fieldCode && !this.contactForm.controls.ContactMethodId.value ) {
      if ( this.contactForm?.get('Email')?.value && this.contactForm?.get('Mobile')?.value ) {
        this.contactForm.controls.ContactMethodId.setValue(3);
      } else if ( !this.contactForm?.get('Email')?.value ) {
        this.contactForm.controls.ContactMethodId.setValue(fieldCode);
      }
    }
  }

  consentChanges(): void {
    this.actionHelper.notifyAboutEdit();

    this.onMarketingChanges.emit({
      consentMarketing: this.consentMarketing,
      consentThirdParties: this.consentThirdParties
    })
  }

  scrollToInvalidContactField(): void {
    const invalidInputs = Object.keys(this.contactForm.controls).filter(item => !!this.contactForm.controls[item].invalid);

    if ( this.needsAtLeastOneEmailOrPhone ) {
      invalidInputs.push('Email');
    }

    if ( this.isInvalidProvinceCode(true) ) {
      invalidInputs.push('ProvinceCode');
    }

    if (
      invalidInputs.includes('LanguageCode') ||
      invalidInputs.includes('Address') ||
      invalidInputs.includes('AddressExt') ||
      invalidInputs.includes('Company') ||
      invalidInputs.includes('ZipCode') ||
      invalidInputs.includes('City') ||
      invalidInputs.includes('CountryCode') ||
      invalidInputs.includes('TaxNumber') ||
      invalidInputs.includes('ProvinceCode') ||
      invalidInputs.includes('VatNumber') ||
      invalidInputs.includes('Website') ||
      invalidInputs.includes('CustomerCodeComposer') ||
      invalidInputs.includes('CustomerCode')
    ) {
      this.showDetails = true;
    }

    setTimeout(() => {
      invalidInputs.forEach(item => {
        this.inputs?.forEach(( input ) => {
          this.checkContainClass(input, item);
        });
      });
    }, 100);

    if ( this.isNewLeadForm ) {
      this.inputs?.forEach(( input: ElementRef<HTMLElement> ) => {
        if ( !this.privacy.contact ) {
          this.checkContainClass(input, 'privacyContact');
        }
        if ( !this.privacy.documented ) {
          this.checkContainClass(input, 'privacyDocumented');
        }
      });
    }
  }

  private checkContainClass( input: ElementRef<HTMLElement>, item: string ): void {
    const nativeElement = input.nativeElement;

    if ( nativeElement?.classList.contains(item) ) {
      nativeElement.scrollIntoView({
        behavior: "smooth",
      });
    }
  }

  private setContactTypesTabs(): void {
    this.tabs.length = 0;

    if ( !this.additionalContact && this.showPrivate ) {
      this.tabs = this.contactTypes.map(( contact ) => {
        return {
          Name: contact.Description,
          Id: contact.Id.toString()
        }
      });
    } else {
      const filteredTypes = this.contactTypes.filter(( contact ) => contact.Id !== ContactHelper.CONTACT_TYPE_PRIVATE);
      this.tabs = filteredTypes.map(( contact ) => {
        return {
          Name: contact.Description,
          Id: contact.Id.toString()
        }
      });

      if ( this.additionalContact && this.tabs.length > 0 ) {
        const firstTab = this.tabs[0];
        if ( firstTab.Id == ContactHelper.CONTACT_TYPE_PLANER.toString() || firstTab.Id == ContactHelper.CONTACT_TYPE_TRADE.toString() ) {
          this.consentMarketing = true;
          this.consentChanges();
        }
      }
    }
  }

  checkIfEmailOrPhoneNeedsToBeFilled( dontMarkAsError = false, test = false ): boolean {
    if (
      !this.contactForm?.get('Email')?.value?.trim() &&
      !this.contactForm?.get('AdditionalEmail')?.value?.trim() &&

      ( !this.contactForm?.get('Phone')?.value?.trim() || !this.contactForm?.get('PhonePrefix')?.value?.trim()) &&
      ( !this.contactForm?.get('Mobile')?.value?.trim() || !this.contactForm?.get('MobilePrefix')?.value?.trim()) &&
      ( !this.contactForm?.get('PhoneOther')?.value?.trim() || !this.contactForm?.get('PhoneOtherPrefix')?.value?.trim())

    ) {
      if ( test ) {
        return true;
      }

      if ( !dontMarkAsError ) {
        this.needsAtLeastOneEmailOrPhone = this.showErrors = true;
        return true;
      }
    } else {
      this.needsAtLeastOneEmailOrPhone = false;
    }

    return false;
  }

  protected readonly ContactHelper = ContactHelper;
}
